/**
 * Preventive Care Service
 * 
 * Calculates patient-specific preventive services that are due based on:
 * - Patient demographics (age, gender)
 * - Medical history (chronic conditions)
 * - Immunization history (time since last vaccination)
 * - Risk factors (pregnancy, immunosuppression, lifestyle factors)
 */

import { Patient } from '../types/patient'
import { Immunisation } from '../types/immunisation'
import {
  PreventiveService,
  PreventiveServiceStatus,
  PreventiveServicePriority,
  RiskFactor,
  CalculatePreventiveServicesInput,
  PreventiveServicesResult,
} from '../types/preventiveCare'

/**
 * Calculate age at a specific date (for testing and consistency)
 * This ensures we use the currentDate parameter instead of always using new Date()
 * 
 * Handles ISO date format (YYYY-MM-DD) which is the standard format used in the system
 */
function calculateAgeAtDate(dateOfBirth: string, atDate: Date): number {
  if (!dateOfBirth) {
    console.warn('Missing date of birth for age calculation')
    return 0
  }
  
  // Parse date of birth - expect ISO format (YYYY-MM-DD)
  // Use Date constructor which handles ISO format correctly
  let birthDate: Date
  if (dateOfBirth.includes('T')) {
    // ISO datetime string (YYYY-MM-DDTHH:mm:ss)
    birthDate = new Date(dateOfBirth)
  } else if (dateOfBirth.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // ISO date string (YYYY-MM-DD) - parse explicitly to avoid timezone issues
    const [year, month, day] = dateOfBirth.split('-').map(Number)
    birthDate = new Date(year, month - 1, day) // month is 0-indexed
  } else {
    // Fallback to Date constructor for other formats
    birthDate = new Date(dateOfBirth)
  }
  
  if (isNaN(birthDate.getTime())) {
    console.warn('Invalid date of birth:', dateOfBirth)
    return 0
  }
  
  // Ensure atDate is valid
  if (isNaN(atDate.getTime())) {
    console.warn('Invalid current date for age calculation, using today')
    atDate = new Date()
  }
  
  let age = atDate.getFullYear() - birthDate.getFullYear()
  const monthDiff = atDate.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && atDate.getDate() < birthDate.getDate())) {
    age--
  }
  
  // Validation: log if age seems incorrect (negative or unreasonably high)
  if (age < 0 || age > 150) {
    console.warn('Age calculation seems incorrect:', {
      dateOfBirth,
      birthDate: birthDate.toISOString(),
      atDate: atDate.toISOString(),
      calculatedAge: age,
    })
  }
  
  return age
}

/**
 * Calculate all preventive services for a patient
 */
export function calculatePreventiveServices(
  input: CalculatePreventiveServicesInput
): PreventiveServicesResult {
  const { patient, immunisations = [], currentDate = new Date() } = input

  const services: PreventiveService[] = []

  // Calculate age using currentDate for consistency
  const age = calculateAgeAtDate(patient.dateOfBirth, currentDate)

  // Debug logging for age calculation issues (only in development)
  if (process.env.NODE_ENV === 'development' && (age < 0 || age > 150)) {
    console.warn('Invalid age calculated:', {
      patientId: patient.id,
      dateOfBirth: patient.dateOfBirth,
      calculatedAge: age,
      currentDate: currentDate.toISOString(),
    })
  }

  // Get chronic conditions (check both legacy and clinical history)
  const chronicConditions = patient.chronicConditions || []

  // Calculate smoking status service
  const smokingService = calculateSmokingStatusService(patient, currentDate)
  if (smokingService) {
    services.push(smokingService)
  }

  // Calculate pneumococcal vaccine recommendations
  const pneumococcalServices = calculatePneumococcalVaccineServices(
    patient,
    age,
    chronicConditions,
    immunisations,
    currentDate
  )
  services.push(...pneumococcalServices)

  // Calculate influenza vaccine recommendations
  const influenzaServices = calculateInfluenzaVaccineServices(
    patient,
    age,
    chronicConditions,
    immunisations,
    currentDate
  )
  services.push(...influenzaServices)

  // Filter due and overdue services
  const dueServices = services.filter(
    (s) => s.status === 'due' || s.status === 'overdue'
  )
  const overdueServices = services.filter((s) => s.status === 'overdue')

  return {
    services,
    dueServices,
    overdueServices,
    dueCount: dueServices.length,
    overdueCount: overdueServices.length,
    totalCount: services.length,
  }
}

/**
 * Calculate smoking status preventive service
 */
function calculateSmokingStatusService(
  patient: Patient,
  currentDate: Date
): PreventiveService | null {
  const oneYearAgo = new Date(
    currentDate.getFullYear() - 1,
    currentDate.getMonth(),
    currentDate.getDate()
  )

  // Check if smoking status is missing
  const isMissing = !patient.smokingStatus && !patient.smokingDetails

  // Check if smoking status was recorded more than a year ago
  let isOutdated = false
  let lastRecordedDate: Date | null = null

  if (patient.smokingStatusLastRecorded) {
    lastRecordedDate = new Date(patient.smokingStatusLastRecorded)
    isOutdated = lastRecordedDate < oneYearAgo
  } else if (patient.smokingDetails) {
    // If smokingDetails exists but smokingStatusLastRecorded is not set,
    // we can't reliably determine when it was last recorded.
    // Treat as outdated to ensure it gets updated with proper timestamp.
    // Note: We don't use updatedAt as a fallback because patient records
    // can be updated for many reasons unrelated to smoking status.
    isOutdated = true
    // Use a conservative estimate: assume it was recorded when smokingDetails was added
    // Since we don't have that date, we'll treat it as needing update
    lastRecordedDate = null
  }

  // Don't create service if status is recorded and recent
  if (!isMissing && !isOutdated) {
    return null
  }

  const riskFactors: RiskFactor[] = []

  const status: PreventiveServiceStatus = isMissing ? 'overdue' : 'overdue'
  const reason = isMissing
    ? 'Smoking status has not been recorded'
    : lastRecordedDate
    ? `Smoking status was last recorded ${formatRelativeDate(lastRecordedDate)}`
    : 'Smoking status needs to be updated with a recorded date'

  return {
    id: `smoking-status-${patient.id}`,
    type: 'assessment',
    name: 'Smoking Status Assessment',
    description:
      'Healthcare professionals should ask and record smoking status at every visit, including type, amount, and history.',
    status,
    priority: 'high',
    reason,
    riskFactors,
    actionUrl: `/patients/${patient.id}/edit#clinical-and-lifestyle`,
    actionLabel: 'Update Smoking Status',
    metadata: {
      lastRecordedDate: lastRecordedDate?.toISOString(),
    },
  }
}

/**
 * Calculate pneumococcal vaccine recommendations
 * 
 * Recommendations based on NIAC guidelines:
 * - Age 65+: Recommend every 5 years
 * - Age 65+ with chronic conditions: Recommend every 5 years (higher priority)
 * - Age <65 with chronic conditions (diabetes, heart disease, lung disease, immunosuppression): Recommend every 5 years
 */
function calculatePneumococcalVaccineServices(
  patient: Patient,
  age: number,
  chronicConditions: string[],
  immunisations: Immunisation[],
  currentDate: Date
): PreventiveService[] {
  const services: PreventiveService[] = []
  const PNEUMOCOCCAL_VACCINE_TYPES = [
    'pneumococcal',
    'PCV13',
    'PCV23',
    'PPV23',
    'Prevenar',
    'Pneumovax',
    'pneumococcal 23-valent',
    'pneumococcal conjugate',
    'pneumococcal polysaccharide',
  ]
  const BOOSTER_INTERVAL_DAYS = 5 * 365 // 5 years in days

  // Check for existing pneumococcal vaccinations
  const pneumococcalImmunisations = immunisations.filter((imm) =>
    PNEUMOCOCCAL_VACCINE_TYPES.some((type) =>
      imm.type.toLowerCase().includes(type.toLowerCase())
    )
  )

  // Find most recent pneumococcal vaccination
  const mostRecentPneumococcal = pneumococcalImmunisations.length > 0
    ? pneumococcalImmunisations.reduce((latest, current) => {
        return new Date(current.date) > new Date(latest.date) ? current : latest
      })
    : null

  // Calculate days since last vaccination
  const daysSinceLastVaccination = mostRecentPneumococcal
    ? Math.floor(
        (currentDate.getTime() - new Date(mostRecentPneumococcal.date).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : Infinity

  // Determine if vaccination is recommended based on age and risk factors
  const riskFactors: RiskFactor[] = []
  let isRecommended = false
  let reason = ''
  let priority: PreventiveServicePriority = 'medium'

  // Age 65+
  if (age >= 65) {
    isRecommended = true
    riskFactors.push({ type: 'age', value: '65+', description: 'Age 65 years or older' })
    priority = 'high'
    
    // Check for chronic conditions
    const hasHighRiskConditions = hasHighRiskChronicConditions(chronicConditions)
    if (hasHighRiskConditions) {
      riskFactors.push({
        type: 'chronic-condition',
        value: 'high-risk',
        description: 'Chronic conditions that increase risk',
      })
      reason = 'Age 65+ with chronic conditions'
    } else {
      reason = 'Age 65+'
    }
    
    // Debug logging in development to help identify age calculation issues
    if (process.env.NODE_ENV === 'development') {
      console.log('Pneumococcal recommendation for age 65+:', {
        patientId: patient.id,
        dateOfBirth: patient.dateOfBirth,
        calculatedAge: age,
      })
    }
  }
  // Age <65 with chronic conditions
  else if (age < 65) {
    const hasHighRiskConditions = hasHighRiskChronicConditions(chronicConditions)
    if (hasHighRiskConditions) {
      isRecommended = true
      riskFactors.push({
        type: 'chronic-condition',
        value: 'high-risk',
        description: 'Chronic conditions that increase risk',
      })
      reason = 'Chronic conditions (under 65)'
      priority = 'high'
    }
  }

  // If recommended, check if booster is due
  if (isRecommended) {
    const isDue =
      !mostRecentPneumococcal || daysSinceLastVaccination >= BOOSTER_INTERVAL_DAYS

    if (isDue) {
      const dueDate = mostRecentPneumococcal
        ? new Date(
            new Date(mostRecentPneumococcal.date).getTime() +
              BOOSTER_INTERVAL_DAYS * 24 * 60 * 60 * 1000
          )
        : currentDate

      const status: PreventiveServiceStatus =
        dueDate < currentDate ? 'overdue' : 'due'

      services.push({
        id: `pneumococcal-${patient.id}`,
        type: 'immunization',
        name: 'Pneumococcal Vaccine',
        description:
          'Pneumococcal vaccination is recommended every 5 years for adults 65 years and older, or for adults with certain chronic conditions.',
        status,
        priority,
        dueDate: dueDate.toISOString().split('T')[0],
        overdueDate:
          status === 'overdue' ? dueDate.toISOString().split('T')[0] : undefined,
        reason,
        riskFactors,
        actionUrl: `#immunisations`, // Hash for tab navigation
        actionLabel: 'Record Immunization',
        metadata: {
          vaccineType: 'pneumococcal',
          daysSinceLastVaccination:
            mostRecentPneumococcal ? daysSinceLastVaccination : undefined,
          recommendedIntervalDays: BOOSTER_INTERVAL_DAYS,
          lastVaccinationDate: mostRecentPneumococcal?.date,
        },
      })
    }
  }

  return services
}

/**
 * Calculate influenza vaccine recommendations
 * 
 * Recommendations based on NIAC guidelines:
 * - Age 65+: Recommend annually (high priority)
 * - Age <65 with chronic conditions: Recommend annually
 * - Pregnancy: Recommend annually (any trimester)
 * - Healthcare workers: Recommend annually (not implemented - requires user role)
 * - All others: Optional (recommend annually)
 */
function calculateInfluenzaVaccineServices(
  patient: Patient,
  age: number,
  chronicConditions: string[],
  immunisations: Immunisation[],
  currentDate: Date
): PreventiveService[] {
  const services: PreventiveService[] = []
  const INFLUENZA_VACCINE_TYPES = [
    'influenza',
    'flu',
    'seasonal flu',
    'flu shot',
    'influenza vaccine',
    'seasonal influenza',
    'flu jab',
  ]
  const ANNUAL_INTERVAL_DAYS = 365 // 1 year

  // Check for existing influenza vaccinations
  const influenzaImmunisations = immunisations.filter((imm) =>
    INFLUENZA_VACCINE_TYPES.some((type) =>
      imm.type.toLowerCase().includes(type.toLowerCase())
    )
  )

  // Find the most recent influenza vaccination from all influenza immunisations
  // This ensures we always use the most recent vaccination for calculating the next due date
  const mostRecentInfluenza =
    influenzaImmunisations.length > 0
      ? influenzaImmunisations.reduce((latest, current) => {
          return new Date(current.date) > new Date(latest.date) ? current : latest
        })
      : null

  // Calculate days since last vaccination
  const daysSinceLastVaccination = mostRecentInfluenza
    ? Math.floor(
        (currentDate.getTime() - new Date(mostRecentInfluenza.date).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : Infinity

  // Determine if vaccination is recommended
  // Note: Influenza is recommended for all patients, but priority varies
  const riskFactors: RiskFactor[] = []
  let reason = ''
  let priority: PreventiveServicePriority = 'low'

  // Age 65+
  if (age >= 65) {
    riskFactors.push({ type: 'age', value: '65+', description: 'Age 65 years or older' })
    reason = 'Age 65+'
    priority = 'high'
    
    // Debug logging in development to help identify age calculation issues
    if (process.env.NODE_ENV === 'development') {
      console.log('Influenza recommendation for age 65+:', {
        patientId: patient.id,
        dateOfBirth: patient.dateOfBirth,
        calculatedAge: age,
      })
    }
  }
  // Age <65 with chronic conditions
  else if (age < 65 && chronicConditions && chronicConditions.length > 0) {
    riskFactors.push({
      type: 'chronic-condition',
      value: 'chronic-conditions',
      description: 'Chronic conditions that increase risk',
    })
    reason = 'Chronic conditions'
    priority = 'high'
  }
  // All others
  else {
    reason = 'Annual influenza vaccination recommended'
    priority = 'medium'
  }

  // Check if vaccination is due
  // A vaccination is due if the last vaccination was more than 365 days ago (or no vaccination exists)
  // If a recent vaccination exists (within last year), don't show as due
  const hasRecentVaccination = mostRecentInfluenza && daysSinceLastVaccination < ANNUAL_INTERVAL_DAYS
  const isDue = !hasRecentVaccination

  if (isDue) {
    const dueDate = mostRecentInfluenza
      ? new Date(
          new Date(mostRecentInfluenza.date).getTime() +
            ANNUAL_INTERVAL_DAYS * 24 * 60 * 60 * 1000
        )
      : currentDate

    const status: PreventiveServiceStatus =
      dueDate < currentDate && !mostRecentInfluenza ? 'overdue' : 'due'

    services.push({
      id: `influenza-${patient.id}`,
      type: 'immunization',
      name: 'Influenza Vaccine',
      description:
        'Annual influenza vaccination is recommended, especially for those 65 years and older or with chronic conditions.',
      status,
      priority,
      dueDate: dueDate.toISOString().split('T')[0],
      overdueDate:
        status === 'overdue' ? dueDate.toISOString().split('T')[0] : undefined,
      reason,
      riskFactors,
      actionUrl: `#immunisations`, // Hash for tab navigation
      actionLabel: 'Record Immunization',
      metadata: {
        vaccineType: 'influenza',
        daysSinceLastVaccination: mostRecentInfluenza ? daysSinceLastVaccination : undefined,
        recommendedIntervalDays: ANNUAL_INTERVAL_DAYS,
        lastVaccinationDate: mostRecentInfluenza?.date,
      },
    })
  }

  return services
}

/**
 * Check if patient has high-risk chronic conditions for pneumococcal vaccination
 */
function hasHighRiskChronicConditions(chronicConditions: string[]): boolean {
  if (!chronicConditions || chronicConditions.length === 0) {
    return false
  }

  const highRiskConditions = [
    'diabetes',
    'heart disease',
    'heart failure',
    'coronary',
    'lung disease',
    'COPD',
    'asthma',
    'chronic obstructive',
    'kidney disease',
    'renal',
    'liver disease',
    'hepatic',
    'immunosuppression',
    'immunosuppressed',
    'HIV',
    'cancer',
    'splenectomy',
  ]

  const conditionsLower = chronicConditions.map((c) => c.toLowerCase())

  return highRiskConditions.some((risk) =>
    conditionsLower.some((condition) => condition.includes(risk))
  )
}

/**
 * Format relative date (e.g., "2 months ago")
 */
function formatRelativeDate(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffYears > 0) {
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`
  } else if (diffMonths > 0) {
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`
  } else {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }
}
